/**
 * OnboardingTour — Interactive guided tour for first-time users.
 * Uses a spotlight overlay + positioned tooltip to highlight UI elements step by step.
 */
import React, {
  createContext, useContext, useState, useEffect,
  useLayoutEffect, useRef, useCallback,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle, Compass } from 'lucide-react';

// ── Storage key ───────────────────────────────────────────────────────────────
const STORAGE_KEY = 'valoriza_tour_v1_completed';

// ── Tour step definitions ──────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'welcome',
    title: '¡Bienvenido al Dashboard de Valoriza!',
    body: 'Este tour rápido te mostrará las principales funcionalidades en menos de 2 minutos. Puedes saltarlo en cualquier momento.',
    target: null,
    position: 'center',
    icon: '👋',
  },
  {
    id: 'sidebar',
    title: 'Navegación principal',
    body: 'Accede a todos los módulos desde aquí: OPS, Liderazgo, Near Misses, Siniestralidad, Correlación y Mapa. El botón circular del borde derecho lo colapsa para ganar espacio.',
    target: '[data-testid="sidebar-nav"]',
    position: 'right',
    icon: '🗂️',
  },
  {
    id: 'filters',
    title: 'Filtros globales con búsqueda',
    body: 'Ajusta el período, sede, negocio o gerente. Todos los gráficos se actualizan al instante. Los selectores de Gerente y Sede incluyen búsqueda integrada: escribe las primeras letras para filtrar.',
    target: '[data-testid="global-filter-bar"]',
    position: 'bottom',
    icon: '🔍',
  },
  {
    id: 'kpis',
    title: 'Indicadores clave (KPIs)',
    body: 'Totales de OPS, Liderazgo, Near Misses y Asistentes del período seleccionado. Pasa el cursor sobre el icono ⓘ de cada tarjeta para ver la definición del indicador.',
    target: '[data-testid="dashboard-kpi-row"]',
    position: 'bottom',
    icon: '📊',
  },
  {
    id: 'siniest',
    title: 'KPIs de Siniestralidad',
    body: 'Accidentes con baja, jornadas perdidas, Índice de Frecuencia (I.F.) e Índice de Gravedad (I.G.) del año en curso. Datos del último Excel importado por el administrador.',
    target: '[data-testid="dashboard-siniest-section"]',
    position: 'top',
    icon: '⚠️',
  },
  {
    id: 'pando',
    title: 'Pando AI — Asistente Inteligente',
    body: 'Haz preguntas en lenguaje natural sobre los datos del dashboard. Pando AI accede a los mismos datos que ves en pantalla. También puedes descargar cada respuesta como documento Word.',
    target: '[data-testid="ai-assistant-button"]',
    position: 'top-left',
    icon: '🤖',
  },
  {
    id: 'finish',
    title: '¡Todo listo!',
    body: 'Ya conoces lo esencial. Recuerda que puedes descargar el Manual de Usuario completo desde el menú de tu avatar (esquina superior derecha). También puedes relanzar este tour desde ese menú.',
    target: null,
    position: 'center',
    icon: '🎉',
  },
];

// ── Context ───────────────────────────────────────────────────────────────────
const TourCtx = createContext(null);
export const useTour = () => useContext(TourCtx);

// ── Tooltip positioning helper ─────────────────────────────────────────────────
function calcTooltipPos(rect, position, tw = 340, th = 200) {
  const pad = 18;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left, top;

  switch (position) {
    case 'right':
      left = rect.right + pad;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    case 'left':
      left = rect.left - tw - pad;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    case 'bottom':
      left = rect.left + rect.width / 2 - tw / 2;
      top = rect.bottom + pad;
      break;
    case 'top':
      left = rect.left + rect.width / 2 - tw / 2;
      top = rect.top - th - pad;
      break;
    case 'top-left':
      left = rect.right - tw;
      top = rect.top - th - pad;
      break;
    case 'bottom-left':
      left = rect.right - tw;
      top = rect.bottom + pad;
      break;
    case 'center':
    default:
      left = vw / 2 - tw / 2;
      top = vh / 2 - th / 2;
      break;
  }

  // Clamp to viewport
  left = Math.max(12, Math.min(left, vw - tw - 12));
  top = Math.max(12, Math.min(top, vh - th - 12));
  return { left, top };
}

// ── TourOverlay ───────────────────────────────────────────────────────────────
function TourOverlay({ onNext, onPrev, onEnd, step, stepIndex, totalSteps }) {
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  // Measure target element on step change
  useLayoutEffect(() => {
    if (!step.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });

    // Scroll element into view if needed
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [step]);

  const pad = 8;
  const spotlight = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  const tooltipPos = spotlight
    ? calcTooltipPos(spotlight, step.position)
    : calcTooltipPos({ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }, 'center');

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="tour-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{ position: 'fixed', inset: 0, zIndex: 9990, pointerEvents: 'all' }}
      >
        {/* Dark backdrop */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.62)', transition: 'all 0.3s' }} />

        {/* Spotlight cutout */}
        {spotlight && (
          <motion.div
            key={`spot-${stepIndex}`}
            initial={false}
            animate={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'absolute',
              borderRadius: 10,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)',
              background: 'transparent',
              border: '2px solid rgba(0,200,83,0.7)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}

        {/* Tooltip card */}
        <motion.div
          ref={tooltipRef}
          key={`tip-${stepIndex}`}
          initial={{ opacity: 0, scale: 0.93, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.93, y: -8 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            left: tooltipPos.left,
            top: tooltipPos.top,
            width: 340,
            zIndex: 9999,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #06152E 0%, #0B2145 100%)',
            border: '1px solid rgba(74,158,255,0.28)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,200,83,0.12)',
            padding: '20px 22px 18px',
            pointerEvents: 'all',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22, lineHeight: 1 }}>{step.icon}</span>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3, margin: 0 }}>
                {step.title}
              </p>
            </div>
            <button
              onClick={onEnd}
              title="Saltar tour"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 6,
                padding: '4px 6px',
                cursor: 'pointer',
                color: 'rgba(170,204,255,0.7)',
                lineHeight: 1,
                flexShrink: 0,
                marginLeft: 8,
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Body */}
          <p style={{ fontSize: 12.5, color: 'rgba(170,210,255,0.85)', lineHeight: 1.65, margin: '0 0 16px' }}>
            {step.body}
          </p>

          {/* Footer: progress + buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Step dots */}
            <div style={{ display: 'flex', gap: 5 }}>
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} style={{
                  width: i === stepIndex ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === stepIndex ? '#00C853' : 'rgba(255,255,255,0.2)',
                  transition: 'all 0.3s',
                }} />
              ))}
            </div>

            {/* Nav buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              {!isFirst && (
                <button
                  onClick={onPrev}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 8,
                    background: 'rgba(74,158,255,0.15)',
                    border: '1px solid rgba(74,158,255,0.3)',
                    color: '#AAD0FF', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <ArrowLeft size={12} />
                  Atrás
                </button>
              )}
              <button
                onClick={isLast ? onEnd : onNext}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 14px', borderRadius: 8,
                  background: isLast ? 'linear-gradient(135deg, #00C853, #4A9EFF)' : '#00C853',
                  border: 'none',
                  color: '#fff', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,200,83,0.4)',
                }}
              >
                {isLast ? (
                  <><CheckCircle size={12} /> ¡Listo!</>
                ) : (
                  <>Siguiente <ArrowRight size={12} /></>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── TourProvider ───────────────────────────────────────────────────────────────
export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setActive(true);
  }, []);

  const next = useCallback(() => {
    setStepIndex(prev => (prev < STEPS.length - 1 ? prev + 1 : prev));
  }, []);

  const prev = useCallback(() => setStepIndex(i => Math.max(0, i - 1)), []);

  const endTour = useCallback(() => {
    setActive(false);
    localStorage.setItem(STORAGE_KEY, '1');
  }, []);

  // Auto-start on first visit: mark as seen immediately so closing mid-tour doesn't re-show
  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, '1'); // Mark seen before starting to prevent repeat on reload
      const t = setTimeout(() => startTour(), 1200);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  return (
    <TourCtx.Provider value={{ startTour, endTour, active }}>
      {children}
      {active && (
        <TourOverlay
          step={STEPS[stepIndex]}
          stepIndex={stepIndex}
          totalSteps={STEPS.length}
          onNext={next}
          onPrev={prev}
          onEnd={endTour}
        />
      )}
    </TourCtx.Provider>
  );
}
